import {
  Box,
  DialogContent,
  Divider,
  Typography,
  useTheme
} from '@mui/material'
import { DialogCloseButton } from 'assets/icons/menu'
import { StyledDialog } from 'components/dialogs/styled.dialog'
import WhatsNewPostsLoader from 'components/loaders/WhatsNewPostsLoader'
import { t } from 'i18next'
import { WhatsNewPost } from 'services/api/whatsNew'

interface WhatsNewDialogProps {
  open: boolean
  setOpen: (b: boolean) => void
  posts: WhatsNewPost[]
  loading: boolean
  error: string | null
  onClose: () => void
}

export default function WhatsNewDialog(props: WhatsNewDialogProps) {
  const theme = useTheme()

  const handleClose = () => {
    props.setOpen(false)
    props.onClose()
  }

  return (
    <StyledDialog
      open={props.open}
      onClose={handleClose}
      maxWidth={'md'}
      fullWidth
      disableScrollLock
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.35)'
                : 'rgba(255, 255, 255, 0.18)'
          }
        }
      }}
      PaperProps={{
        style: {
          borderRadius: '20px',
          backgroundImage: 'none'
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <DialogContent
          dividers={false}
          sx={{
            height: '80vh',
            paddingTop: 4,
            '& img': {
              maxWidth: '100%',
              height: 'auto'
            },
            '& iframe': {
              maxWidth: '100%',
              width: '100%',
              minHeight: 320,
              border: 0
            },
            '& a': {
              color: 'var(--app-accent-color)'
            },
            '& h1, & h2, & h3, & h4': {
              marginTop: 0,
              marginBottom: '1.5rem'
            },
            '& p': {
              lineHeight: 1.7,
              marginTop: 0,
              marginBottom: '1.25rem'
            },
            '& ul, & ol': {
              marginTop: 0,
              marginBottom: '1.25rem',
              paddingInlineStart: '1.75rem'
            },
            '& li': {
              lineHeight: 1.7,
              marginBottom: '0.125rem'
            },
            '& li p': {
              marginTop: 0,
              marginBottom: '0.125rem',
              lineHeight: 1.7
            },
            '& li:last-child': {
              marginBottom: 0
            },
            '& li:last-child p:last-child': {
              marginBottom: 0
            }
          }}
        >
          <DialogCloseButton
            style={{ position: 'absolute', right: 16, top: 16 }}
            onClick={handleClose}
          />
          {props.loading ? (
            <Box sx={{ paddingTop: 1 }}>
              <WhatsNewPostsLoader />
            </Box>
          ) : props.error ? (
            <Typography color={theme.palette.error.main}>
              {props.error}
            </Typography>
          ) : props.posts.length === 0 ? (
            <Typography color={theme.palette.input.placeholder}>
              {t('noData', { defaultValue: 'No updates yet.' })}
            </Typography>
          ) : (
            props.posts.map((post, index) => (
              <Box key={post.id}>
                <Box
                  dangerouslySetInnerHTML={{ __html: post.content.rendered }}
                />
                {index < props.posts.length - 1 ? (
                  <Divider
                    sx={{
                      marginY: '3rem',
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.3)'
                          : 'rgba(0, 0, 0, 0.35)',
                      borderBottomWidth: 'inherit'
                    }}
                  />
                ) : null}
              </Box>
            ))
          )}
        </DialogContent>
      </Box>
    </StyledDialog>
  )
}
